import React, { useRef, useEffect, useState } from 'react'
import { Route, Switch, Redirect, useLocation } from 'react-router-dom'
import DriveDashboardPage from 'components/pages/DriveDashboard/DriveDashboardPage'
import MFA from 'components/pages/MFA'
import RestrictedAccess from 'rui/drive/RestrictedAccess'
import SignUp from 'rui/drive/SignUp'
import SignIn from 'components/pages/SignIn'
import ForgotPassword from 'components/pages/ForgotPassword'
import PhoneVerification from 'components/pages/PhoneVerification'
import Terms from 'components/pages/Terms'
import ResetPasswordPage from 'components/pages/account/ResetPassword/ResetPasswordPage'
import BasicInfoPage from 'components/pages/account/BasicInfo/BasicInfoPage'
import DrivingInfoPage from 'components/pages/account/DrivingInfo/DrivingInfoPage'
import BankInfoPage from 'components/pages/account/BankInfo/BankInfoPage'
import PhotoAndBioPage from 'components/pages/account/PhotoAndBio/PhotoAndBioPage'
import Background from 'components/pages/Background'
import Disclosures from 'components/pages/Disclosures'
import SsnStep from 'components/pages/SsnStep'
import GigHistoryPage from 'components/pages/GigHistory/GigHistoryPage'
import EarningsStatementPage from 'components/pages/EarningsStatement/EarningsStatementPage'
import ConsolidationDetailsPage from 'components/pages/DeliveryDetails/ConsolidationDetailsPage'
import TaxInfoPage from 'components/pages/TaxInfo/TaxInfoPage'
import DownloadPage from 'components/pages/Download/DownloadPage'
import HowTosPage from 'components/pages/HowTos/HowTosPage'
import PromotionsPage from 'components/pages/Promotions/PromotionsPage'
import BlockedMessagePage from 'components/pages/BlockedMessage/BlockedMessagePage'
import EmailAndPhonePage from 'components/pages/account/EmailAndPhone/EmailAndPhonePage'
import EditAccountNavPage from 'components/pages/EditAccountNav/EditAccountNavPage'
import DeleteAccountPage from 'components/pages/account/DeleteAccount/DeleteAccountPage'
import { driveNeedsSelector, driverNeeds } from 'selectors'
import { connect } from 'react-redux'
import { sendNativeMessage } from 'util/native'
import { StaticLoader } from 'components/misc/Loader'
import Gtm from 'util/gtm'
import GoMobile from 'components/pages/GoMobile'
import Prop22Page from 'components/pages/certifications/Prop22/Prop22Page'
import TrailerHitchPage from 'components/pages/certifications/TrailerHitch/TrailerHitchPage'
import OnboardingPage from 'components/pages/certifications/Onboarding/OnboardingPage'
import PharmacyPage from 'components/pages/certifications/Pharmacy/PharmacyPage'
import ReturnsPage from 'components/pages/certifications/Returns/ReturnsPage'
import CommittedCapacityPage from 'components/pages/certifications/CommittedCapacity/CommittedCapacityPage'
import OverTheThresholdPage from 'components/pages/certifications/OverTheThreshold/OverTheThresholdPage'
import RoadieXDPage from 'components/pages/certifications/RoadieXD/RoadieXDPage'
import MailInnovationsPage from 'components/pages/certifications/MailInnovations/MailInnovationsPage'
import VerifyIdentity from 'components/pages/VerifyIdentity'
import SeattleDashboardPage from 'components/pages/SeattleDashboard/SeattleDashboardPage'
import sentrySetup from 'sentry_setup'
import NewVehiclePage from 'components/pages/account/DrivingInfo/NewVehicle/NewVehiclePage'
import VehicleInfoPage from 'components/pages/account/DrivingInfo/VehicleInfo/VehicleInfoPage'
import BasicInfoRegistration from 'components/pages/account/BasicInfo/BasicInfoRegistration'
import LanguagePreferencePage from 'components/pages/account/LanguagePreference/LanguagePreferencePage'
import PrivacySettingsPage from 'components/pages/account/PrivacySettings/PrivacySettingsPage'
import DuplicateAccount from 'components/pages/DuplicateAccount'
import ReturnRatePage from 'components/pages/Dashboard/ReturnRate/ReturnRatePage'
import CommitmentRatePage from 'components/pages/Dashboard/CommitmentRate/CommitmentRatePage'
import OnTimeRatePage from 'components/pages/Dashboard/OnTimeRate/OnTimeRatePage'
import BlockDetailsPage from 'components/pages/BlockPayments/BlockDetailsPage'
import IDVHelpFormPage from 'components/pages/Dashboard/Salesforce/IDV/IDVHelpFormPage'
import IDVHelpStatusPage from 'components/pages/Dashboard/Salesforce/IDV/IDVHelpStatusPage'
import AccountStatusPage from 'components/pages/Dashboard/AccountStatus'
import AppealFormPage from 'components/pages/Dashboard/Salesforce/Appeal/AppealFormPage'
import AppealStatusPage from 'components/pages/Dashboard/Salesforce/Appeal/AppealStatusPage'
import BlocksOnboardingPage from 'components/pages/BlocksOnboarding/BlocksOnboardingPage'

const createInject = (propsToInject) => (Comp) => (props) => <Comp {...propsToInject} {...props} />

const needsComps = {
  'verify-phone': PhoneVerification,
  terms: Terms,
  'basic-info': BasicInfoRegistration,
  'duplicate-account': DuplicateAccount,
  'driving-info': DrivingInfoPage,
  'blocked-message': BlockedMessagePage,
  'go-mobile': GoMobile,
  'verify-identity': VerifyIdentity,
  'consumer-report': Background,
  disclosures: Disclosures,
  'bank-info': BankInfoPage,
  'profile-picture': PhotoAndBioPage,
  onboarding: OnboardingPage,
  'restricted-access': RestrictedAccess,
  'language-preference': LanguagePreferencePage,
  'privacy-settings': PrivacySettingsPage,
  'return-rate': ReturnRatePage,
  'commitment-rate': CommitmentRatePage,
  ssn: SsnStep,
}

function usePrevious(value) {
  const ref = useRef()
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}

const DriveRouter = connect(driveNeedsSelector)((props) => {
  const {
    dispatch,
    mfa,
    profile,
    authenticityVerificationState,
    isWebview,
    lockReason,
    termsDocuments,
    disclosureDocuments,
  } = props
  const needs = driverNeeds(
    mfa,
    profile,
    lockReason,
    isWebview,
    authenticityVerificationState,
    termsDocuments,
    disclosureDocuments
  )
  const hadNeeds = usePrevious(!!needs)
  const location = useLocation()
  const [gtmEventSent, setGtmEventSent] = useState(false)

  useEffect(() => {
    if (hadNeeds && !needs) {
      dispatch({ type: 'WAS_SIGNING_UP' })
    }
  }, [hadNeeds, needs, dispatch])

  useEffect(() => {
    sentrySetup(import.meta.env.REACT_APP_SENTRY_DRIVE)
  }, [])
  const redirectPath = React.useCallback(
    (path) => {
      return [isWebview ? '/webview' : '', path].join('/')
    },
    [isWebview]
  )
  const routePath = React.useCallback((path) => {
    return [`/${path}`, `/webview/${path}`]
  }, [])

  useEffect(() => {
    let isMounted = true // Tracking to prevent unecessary update if component unmounts

    if (isWebview && props.wasSigningUp && !gtmEventSent) {
      // Push event to dataLayer and wait for GTM to process it before closing webview
      Gtm.pushAsync({ event: 'tutorial_complete' }).then(() => {
        if (isMounted) {
          sendNativeMessage({ type: 'COMPLETE_REGISTRATION' })
          setGtmEventSent(true)
        }
      })
    }
    return () => {
      isMounted = false
    }
  }, [isWebview, props.wasSigningUp, gtmEventSent])

  // Return loader while mobile processes 'COMPLETE_REGISTRATION' message to close webview
  if (isWebview && props.wasSigningUp) {
    return <StaticLoader />
  }

  const inject = createInject({
    app: 'drive',
    registered: props.registered,
  })

  if (needs === 'mfa') {
    // Multi Factor Authentication
    const mfaFlash = props.flash && props.flash.id === 'mfa' ? props.flash : null
    return (
      <Switch>
        <Route path={routePath('multifactor-auth')} exact component={inject(MFA)} />
        <Redirect to={{ pathname: redirectPath('multifactor-auth'), state: { flash: mfaFlash } }} />
      </Switch>
    )
  } else if (needs === 'user') {
    // Not signed in
    // FIX ME setting the flash here is a hack bc the redirect happens after the
    // mfa flash is set, causing it to clear. So we need to set it here as part of the
    // redirect for the user to see it after the redirect.
    const mfaFlash = props.flash && props.flash.id === 'mfa' ? props.flash : null
    return (
      <Switch>
        <Route path={routePath('sign-in')} exact component={inject(SignIn)} />
        <Route path={routePath('sign-up')} exact component={inject(SignUp)} />
        <Route path={routePath('forgot-password')} component={inject(ForgotPassword)} />
        <Redirect to={{ pathname: redirectPath('sign-in'), state: { flash: mfaFlash } }} />
      </Switch>
    )
  } else if (needs) {
    // Onboarding needs not met
    const NeedsComp = inject(needsComps[needs])
    const path = `sign-up/${needs}`

    // Allow exceptions to the onboarding flow for certain paths
    const exceptionPaths = [
      routePath('account/idv-help-request'),
      routePath('account/idv-help-status'),
      routePath('account/account-status'),
      routePath('account/appeal'),
      routePath('account/appeal-status'),
    ]
    const isExceptionPath = exceptionPaths.some((p) => p.includes(location.pathname))
    if (isExceptionPath) {
      return (
        <Switch>
          <Route path={routePath('account/idv-help-request')} component={inject(IDVHelpFormPage)} />
          <Route path={routePath('account/idv-help-status')} component={inject(IDVHelpStatusPage)} />
          <Route path={routePath('account/account-status')} component={inject(AccountStatusPage)} />
          <Route path={routePath('account/appeal')} component={inject(AppealFormPage)} />
          <Route path={routePath('account/appeal-status')} component={inject(AppealStatusPage)} />
        </Switch>
      )
    }

    // Continue on the onboarding flow
    if (props.registered) {
      return <NeedsComp needsPage={true} />
    }
    return (
      <Switch>
        <Route path={routePath(path)}>
          <NeedsComp />
        </Route>
        <Redirect to={redirectPath(path)} />
      </Switch>
    )
  }
  // All good
  return (
    <Switch>
      <Route path={routePath('blank')} component={() => null} />
      <Route path={routePath('dashboard')} component={inject(DriveDashboardPage)} />
      <Route path={routePath('gig-history')} component={inject(GigHistoryPage)} />
      <Route path={routePath('delivery/:consolidation_id')} component={inject(ConsolidationDetailsPage)} />
      <Route path={routePath('tax-information')} component={inject(TaxInfoPage)} />
      <Route path={routePath('download/:fileType/:itemId')} component={inject(DownloadPage)} />
      <Route path={routePath('how-tos')} component={inject(HowTosPage)} />
      <Route path={routePath('certifications/prop22')} component={inject(Prop22Page)} />
      <Route path={routePath('certifications/trailer-hitch')} component={inject(TrailerHitchPage)} />
      <Route path={routePath('certifications/roadieblocks')} component={inject(CommittedCapacityPage)} />
      <Route path={routePath('certifications/onboarding')} component={inject(OnboardingPage)} />
      <Route path={routePath('certifications/pharmacy')} component={inject(PharmacyPage)} />
      <Route path={routePath('certifications/returns')} component={inject(ReturnsPage)} />
      <Route path={routePath('certifications/threshold')} component={inject(OverTheThresholdPage)} />
      <Route path={routePath('certifications/roadiexd')} component={inject(RoadieXDPage)} />
      <Route path={routePath('certifications/mail-innovations')} component={inject(MailInnovationsPage)} />
      <Route path={routePath('promotions')} component={inject(PromotionsPage)} />
      <Route path={routePath('mobile-edit-account-nav')} component={inject(EditAccountNavPage)} />
      <Route path={routePath('blocked-message')} component={inject(BlockedMessagePage)} />
      <Route path={routePath('earnings-statement')} component={inject(EarningsStatementPage)} />
      <Route path={routePath('seattle-dashboard')} component={inject(SeattleDashboardPage)} />
      <Route path={routePath('account/contact-info')} component={inject(EmailAndPhonePage)} />
      <Route path={routePath('account/password')} component={inject(ResetPasswordPage)} />
      <Route path={routePath('account/basic-info')} component={inject(BasicInfoPage)} />
      <Route path={routePath('account/driving-info/new-vehicle')} component={inject(NewVehiclePage)} />
      <Route path={routePath('account/driving-info/vehicle-info/:vehicle_id')} component={inject(VehicleInfoPage)} />
      <Route path={routePath('account/driving-info')} component={inject(DrivingInfoPage)} />
      <Route path={routePath('account/bank-info')} component={inject(BankInfoPage)} />
      <Route path={routePath('account/bio')} component={inject(PhotoAndBioPage)} />
      <Route path={routePath('account/delete')} component={inject(DeleteAccountPage)} />
      <Route path={routePath('account/language-preference')} component={inject(LanguagePreferencePage)} />
      <Route path={routePath('account/privacy-settings')} component={inject(PrivacySettingsPage)} />
      <Route path={routePath('account/account-status')} component={inject(AccountStatusPage)} />
      <Route path={routePath('account/appeal')} component={inject(AppealFormPage)} />
      <Route path={routePath('account/appeal-status')} component={inject(AppealStatusPage)} />
      <Route path={routePath('account/idv-help-request')} component={inject(IDVHelpFormPage)} />
      <Route path={routePath('account/idv-help-status')} component={inject(IDVHelpStatusPage)} />
      <Route path={routePath('terms')} component={inject(Terms)} />
      <Route path={routePath('disclosures')} component={inject(Disclosures)} />
      <Route path={routePath('return-rate')} component={inject(ReturnRatePage)} />
      <Route path={routePath('commitment-rate')} component={inject(CommitmentRatePage)} />
      <Route path={routePath('on-time-rate')} component={inject(OnTimeRatePage)} />
      <Route path={routePath('block-details/:block_id')} component={inject(BlockDetailsPage)} />
      <Route path={routePath('blocks-onboarding')} component={inject(BlocksOnboardingPage)} />

      <Redirect to={redirectPath('dashboard')} />
    </Switch>
  )
})

export default DriveRouter
